import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSend, FiCheck } from 'react-icons/fi';
import confetti from 'canvas-confetti';
import posthog from 'posthog-js';

import { shareMatematly } from '../../utils/shareMatematly.js';

import odznakaBrazowa from '../../img/icons/odznaka_brazowa.png';
import odznakaSrebrna from '../../img/icons/odznaka_srebrna.png';
import odznakaZlota from '../../img/icons/odznaka_zlota.png';

import { pluralizeDays, pluralizeFreezes } from '../../utils/plural.js';

const REWARD_AMOUNT = 1;

const badgeConfigs = {
    "Brązowa": {
        image: odznakaBrazowa,
        accentColor: '#A0522D',
        message: `Masz za sobą najtrudniejszy moment — czyli początek. Oby tak dalej! W nagrodę zdobywasz ${REWARD_AMOUNT} dodatkowe ${pluralizeFreezes(REWARD_AMOUNT)} serii.`,
    },
    "Srebrna": {
        image: odznakaSrebrna,
        accentColor: '#607080',
        message: `Jesteś wzorem systematyczności. Konsekwencja to Twoje drugie imię. W nagrodę: ${REWARD_AMOUNT} dodatkowe ${pluralizeFreezes(REWARD_AMOUNT)} serii.`,
    },
    "Złota": {
        image: odznakaZlota,
        accentColor: '#B8860B',
        message: `Mistrzostwo w budowaniu nawyku. Jesteś legendą! W nagrodę: ${REWARD_AMOUNT} dodatkowe ${pluralizeFreezes(REWARD_AMOUNT)} serii.`,
    },
};

const BadgeAchievementPopup = ({ badgeData, onClose }) => {
    const config = badgeConfigs[badgeData.level_name];
    const [shareCopied, setShareCopied] = useState(false);

    const handleShare = async () => {
        const result = await shareMatematly();
        posthog.capture('badge_share_clicked', {
            badge_level: badgeData.level_name,
            streak_count: badgeData.streak_count,
            result,
        });
        if (result === 'copied') {
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 2500);
        }
    };

    useEffect(() => {
        posthog.capture('badge_popup_viewed', {
            badge_level: badgeData.level_name,
            streak_count: badgeData.streak_count,
        });
        confetti({
            particleCount: 160,
            spread: 90,
            origin: { y: 0.55 },
            colors: ['#F8AB3B', '#E09F2E', '#233855', '#ffffff'],
            gravity: 0.8,
            zIndex: 10000,
        });
    }, []);

    if (!config) return null;

    return ReactDOM.createPortal(
        <AnimatePresence>
            <div className="fixed inset-0 z-[9998] flex items-center justify-center px-4">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                    onClick={onClose}
                />

                <motion.div
                    initial={{ scale: 0.92, opacity: 0, y: 12 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    transition={{ type: "spring", damping: 24, stiffness: 280 }}
                    className="relative z-10 bg-white w-full max-w-sm rounded-2xl p-6 shadow-[0_8px_30px_-5px_rgba(35,56,85,0.15)] border border-gray-100 text-center"
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        onClick={onClose}
                        className="absolute top-3 right-3 p-2 text-gray-300 hover:text-gray-500 transition-colors rounded-full hover:bg-gray-50"
                    >
                        <FiX size={20} />
                    </button>

                    {/* Tytuł */}
                    <motion.h2
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-2xl font-extrabold font-apotek tracking-widest uppercase text-[#233855] mb-1"
                    >
                        Nowa odznaka!
                    </motion.h2>
                    {/* <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.15 }}
                        className="text-sm text-gray-400 mb-5"
                    >
                        Zdobyłeś odznakę za systematyczność
                    </motion.p> */}

                    {/* Odznaka */}
                    <motion.div
                        initial={{ scale: 0.75, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 14 }}
                        className="flex flex-col items-center mb-4"
                    >
                        <div className="relative">
                            <img
                                src={config.image}
                                alt={`Odznaka ${badgeData.level_name}`}
                                className="w-36 h-36 object-contain drop-shadow-lg"
                            />
                            {badgeData.streak_count > 0 && (
                                <span
                                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[55%] font-apotek font-bold text-5xl"
                                    style={{ color: config.accentColor, textShadow: '0px 2px 4px rgba(0,0,0,0.2)' }}
                                >
                                    {badgeData.streak_count}
                                </span>
                            )}
                        </div>

                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.35 }}
                            className="text-lg font-bold mt-3"
                            style={{ color: config.accentColor }}
                        >
                            Odznaka {badgeData.level_name}
                        </motion.p>
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="text-sm text-gray-400 mt-0.5"
                        >
                            za {badgeData.streak_count} {pluralizeDays(badgeData.streak_count)} serii
                        </motion.p>
                    </motion.div>

                    {/* Nagroda — zdegradowana do jednej linijki */}
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.45 }}
                        className="text-xs font-semibold text-gray-400 mt-1 mb-6"
                    >
                        ❄️ +{REWARD_AMOUNT} {pluralizeFreezes(REWARD_AMOUNT)} serii
                    </motion.p>

                    {/* Zachęta do polecenia — akcja pierwszoplanowa */}
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className="text-sm text-gray-500 mb-3 leading-relaxed"
                    >
                        Znasz kogoś, kto też walczy z matmą?
                    </motion.p>

                    <motion.button
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.55 }}
                        onClick={handleShare}
                        className="w-full py-3.5 rounded-xl bg-[#E09F2E] text-[#233855] font-bold font-apotek tracking-widest uppercase text-sm hover:bg-[#cc8f29] active:scale-[0.98] transition-all shadow-lg shadow-[#E09F2E]/30 flex items-center justify-center gap-2"
                    >
                        {shareCopied ? (
                            <>
                                <FiCheck size={18} />
                                Skopiowano link
                            </>
                        ) : (
                            <>
                                <FiSend size={18} />
                                Podeślij znajomemu
                            </>
                        )}
                    </motion.button>

                    <motion.button
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.65 }}
                        onClick={onClose}
                        className="w-full mt-3 py-2 text-sm font-semibold text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        Może później
                    </motion.button>
                </motion.div>
            </div>
        </AnimatePresence>,
        document.body
    );
};

export default BadgeAchievementPopup;
